// src/components/kuestion/shared/format.js
// Small presentation helpers shared by the examiner and student apps.
//
// Every rendered time goes through the effective zone and the clock
// preference (Preferences & Timezone contract v1.2 §13): Intl with an
// explicit timeZone, never Date#getHours, and every time is named.
import { effectiveZone, partsIn, clockIn, zoneOffset, instantOf, getDisplay } from './display';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const parseDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "13 Sep 2026"
export const fmtDate = (v) => {
  const d = parseDate(v);
  if (!d) return '—';
  const p = partsIn(d);
  return `${p.day} ${MONTHS[p.month - 1]} ${p.year}`;
};

// "13 Sep, 17:00"
export const fmtDateTime = (v) => {
  const d = parseDate(v);
  if (!d) return '—';
  const p = partsIn(d);
  return `${p.day} ${MONTHS[p.month - 1]}, ${clockIn(d)}`;
};

// "Tuesday 15 September"
export const fmtLongDay = (v = new Date()) => {
  const d = parseDate(v) || new Date();
  const p = partsIn(d);
  return `${DAYS[p.weekday]} ${p.day} ${MONTHS_LONG[p.month - 1]}`;
};

// For <input type="datetime-local">: wall-clock time in the EFFECTIVE zone,
// not the machine zone, so the window an examiner types is the one they see.
export const toLocalInput = (v) => {
  const d = parseDate(v);
  if (!d) return '';
  const t = partsIn(d);
  const p = (n) => String(n).padStart(2, '0');
  return `${t.year}-${p(t.month)}-${p(t.day)}T${p(t.hour)}:${p(t.minute)}`;
};

// The wire is always UTC: the typed wall-clock time becomes the instant at
// which the effective zone reads that time.
export const fromLocalInput = (s) => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  if (!m) return null;
  const d = instantOf(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]));
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

// The effective zone, named — "GMT+5". Every deadline a student or examiner
// sees carries it (backend response B3): "closes 17:00" is ambiguous to a
// cohort that is not all in one place. Reads "UTC" when UTC was reached as
// the last resort, so nobody assumes it is local.
export const zoneLabel = (d = new Date()) => zoneOffset(effectiveZone(), d);
export const displayZone = () => getDisplay().zone;

// "13 Sep 17:00 (GMT+5)"
export const fmtDateTimeZ = (v) => {
  const d = parseDate(v);
  if (!d) return '—';
  const p = partsIn(d);
  const z = zoneLabel(d);
  return `${p.day} ${MONTHS[p.month - 1]} ${clockIn(d)}${z ? ` (${z})` : ''}`;
};

// "17:00 (GMT+5)" for a date that is today
const fmtClockZ = (d) => {
  const z = zoneLabel(d);
  return `${clockIn(d)}${z ? ` (${z})` : ''}`;
};

// "Closes in 6 hours, at 17:00 (GMT+5)" / "Opens 22 Sep 09:00 (GMT+5)" /
// "No window — open indefinitely"
export const relativeWindow = (opensAt, closesAt, now = Date.now()) => {
  const o = parseDate(opensAt);
  const c = parseDate(closesAt);
  if (o && o.getTime() > now) return `Opens ${fmtDateTimeZ(o)}`;
  if (c) {
    const ms = c.getTime() - now;
    if (ms <= 0) return `Closed ${fmtDateTimeZ(c)}`;
    const h = Math.round(ms / 3600000);
    if (h < 1) return `Closes in ${Math.max(1, Math.round(ms / 60000))} min, at ${fmtClockZ(c)}`;
    if (h < 48) return `Closes in ${h} hour${h === 1 ? '' : 's'}, at ${fmtClockZ(c)}`;
    return `Closes ${fmtDateTimeZ(c)}`;
  }
  return 'No window — open indefinitely';
};

export const isOpenNow = (opensAt, closesAt, now = Date.now()) => {
  const o = parseDate(opensAt);
  const c = parseDate(closesAt);
  if (o && o.getTime() > now) return false;
  if (c && c.getTime() <= now) return false;
  return true;
};

export const greeting = (now = new Date()) => {
  const h = partsIn(now).hour;
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

export const initials = (first = '', last = '', fallback = '?') => {
  const s = `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
  return s || fallback;
};

export const fullName = (s) => (s ? `${s.first_name || ''} ${s.last_name || ''}`.trim() : '');

export const pct = (n, digits = 1) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return Math.round(v * 10 ** digits) / 10 ** digits;
};

// mm:ss or h:mm:ss
export const fmtClock = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const p = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${p(m)}:${p(r)}` : `${p(m)}:${p(r)}`;
};

// Mode strings: the contract writes "proctored" and "self-assessment"; accept
// either spelling from the server and always send the underscore form.
export const MODE = { PROCTORED: 'proctored', SELF: 'self_assessment' };
export const normaliseMode = (m) => (String(m || '').toLowerCase().startsWith('proc') ? MODE.PROCTORED : MODE.SELF);
export const isProctored = (m) => normaliseMode(m) === MODE.PROCTORED;
export const modeLabel = (m) => (isProctored(m) ? 'Proctored' : 'Self-assessment');

export const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};

// Triggers a browser download of a Blob or a string.
export const downloadBlob = (data, filename, type = 'text/plain') => {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Minimal CSV: quotes any cell containing a comma, quote or newline.
export const toCsv = (rows) => rows.map((r) => r.map((c) => {
  const s = c == null ? '' : String(c);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}).join(',')).join('\r\n');
